/**
 * Path: packages/frontend/src/core/services/AiExportService.ts
 * Task: Compiles the entire project graph into a highly structured, semantic Markdown document
 *       optimized for Large Language Models (Gemini, NotebookLM, ChatGPT).
 * Update: Decoupled Markdown generation from file downloading to allow direct AI prompting.
 */
import { api, type ApiNode, type ApiEdge } from '@/core/services/api';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';

export class AiExportService {

  /**
   * Generates a semantic Markdown string representing the entire project structure.
   * This is used both for file exports and as the context prompt for the AI Chatbot.
   */
  static async generateProjectMarkdown(projectId: string): Promise<string> {
    const projectStore = useProjectStore();
    const authStore = useAuthStore();

    // 1. Fetch project metadata and graph data
    const [datastoreRes, metaRes] = await Promise.all([
      api.datastore.get(projectId),
      api.project.getMetadata()
    ]);

    const projectMeta = metaRes.projectsMetadata?.[projectId];
    const projectName = projectMeta?.displayName || projectId;
    const projectSummary = projectMeta?.summary || 'No summary provided.';

    const nodes: ApiNode[] = datastoreRes.nodes || [];
    const edges: ApiEdge[] = datastoreRes.edges || [];

    // Create quick lookup maps
    const nodeMap = new Map<string, ApiNode>(nodes.map(n => [n.id, n]));
    const appData = authStore.appData;

    // 2. Build Markdown Document
    let md = `# Project Context: ${projectName}\n\n`;
    md += `**Project ID:** ${projectId}\n`;
    md += `**Export Date:** ${new Date().toISOString()}\n\n`;
    md += `## Project Summary\n${projectSummary}\n\n`;
    md += `---\n\n`;
    md += `# Domain Model (Entities & Relationships)\n\n`;

    // Group nodes by Entity Type
    const groupedNodes: Record<string, ApiNode[]> = {};
    nodes.forEach(node => {
      if (!groupedNodes[node.type]) groupedNodes[node.type] = [];
      groupedNodes[node.type].push(node);
    });

    // Iterate through each entity type
    for (const [type, typeNodes] of Object.entries(groupedNodes)) {
      // Try to find the human-readable name of the type
      const entityDef = appData?.entities?.find((e: any) => e.name === type);
      const typeLabel = entityDef?.title || type.toUpperCase();

      md += `## Entity Category: ${typeLabel}\n\n`;

      // Sort nodes by rank or title
      typeNodes.sort((a, b) => (a.rank || 0) - (b.rank || 0));

      typeNodes.forEach(node => {
        md += `### ${node.title || 'Untitled'} (ID: ${node.id})\n`;

        if (node.attributes?.description) {
          md += `**Description:**\n${node.attributes.description}\n\n`;
        }

        // Print basic attributes
        md += `**Attributes:**\n`;
        md += `- **System Status/Type:** ${node.system || 'Custom'}\n`;
        if (node.version) md += `- **Version:** ${node.version}\n`;

        for (const [key, value] of Object.entries(node.attributes)) {
          if (key === 'description' || !value || (Array.isArray(value) && value.length === 0)) continue;

          // Try to resolve human readable field names
          const fieldDef = appData?.fields?.find((f: any) => f.id === key);
          const fieldLabel = fieldDef?.name || key;

          md += `- **${fieldLabel}:** ${Array.isArray(value) ? value.join(', ') : value}\n`;
        }
        md += `\n`;

        // Print Relationships (Edges)
        const outgoingEdges = edges.filter(e => e.source === node.id);
        const incomingEdges = edges.filter(e => e.target === node.id);

        if (outgoingEdges.length > 0 || incomingEdges.length > 0) {
          md += `**Relationships:**\n`;

          // Handle outgoing edges (Successors, RACI assignments, standard relations)
          outgoingEdges.forEach(edge => {
            const targetNode = nodeMap.get(edge.target);
            const targetName = targetNode?.title || edge.target;

            if (edge.relationId === 'cairo' && edge.meta?.raci) {
              md += `- **RACI (${edge.meta.raci.toUpperCase()}):** ${targetName}\n`;
            } else if (edge.relationId === 'predecessors') {
              md += `- **Successor (Next Step):** ${targetName}\n`;
            } else {
              md += `- **Links to (${edge.relationId}):** ${targetName}\n`;
            }
          });

          // Handle incoming edges (Predecessors)
          incomingEdges.forEach(edge => {
            const sourceNode = nodeMap.get(edge.source);
            const sourceName = sourceNode?.title || edge.source;

            if (edge.relationId === 'predecessors') {
              md += `- **Predecessor (Previous Step):** ${sourceName}\n`;
            } else if (edge.relationId === 'cairo' && edge.meta?.raci) {
              md += `- **Is ${edge.meta.raci.toUpperCase()} for:** ${sourceName}\n`;
            } else {
              md += `- **Linked from (${edge.relationId}):** ${sourceName}\n`;
            }
          });
          md += `\n`;
        }
        md += `---\n\n`;
      });
    }

    return md;
  }

  /**
   * Fetches all project data and triggers a download of a Markdown file.
   */
  static async exportProjectToMarkdown(projectId: string): Promise<void> {
    const md = await this.generateProjectMarkdown(projectId);

    const projectStore = useProjectStore();
    const projectMeta = projectStore.projectsMetadata?.[projectId];
    const projectName = projectMeta?.displayName || projectId;

    // Trigger Download
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Format YYYY-MM-DD
    const dateStr = new Date().toISOString().split('T')[0];
    link.download = `Unravel_${projectName.replace(/[^a-z0-9]/gi, '_')}_AI_Export_${dateStr}.md`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
